import React, { useRef, forwardRef, useEffect, useState } from "react";

/**
 * Component CustomInput giải quyết vấn đề mất focus khi nhập vào input
 *
 * @param {string} value - Giá trị hiện tại của input (controlled component)
 * @param {function} onChange - Hàm xử lý khi giá trị thay đổi
 * @param {string} defaultValue - Giá trị mặc định của input (uncontrolled component)
 * @param {function} onBlur - Hàm xử lý khi blur khỏi input
 * @param {string} type - Loại input (text, url, email, v.v.)
 * @param {string} placeholder - Placeholder của input
 * @param {string} className - Class CSS bổ sung
 * @param {function} onKeyDown - Hàm xử lý khi nhấn phím
 * @returns {JSX.Element}
 */
const CustomInput = forwardRef(
  (
    {
      value,
      onChange,
      defaultValue = "",
      onBlur,
      type = "text",
      placeholder = "",
      className = "",
      onKeyDown,
    },
    ref
  ) => {
    const internalRef = useRef(null);
    const inputRef = ref || internalRef;
    // Sử dụng state nội bộ để theo dõi giá trị và tránh re-render không cần thiết
    const [internalValue, setInternalValue] = useState(
      value !== undefined ? value : defaultValue
    );
    // Sử dụng ref để theo dõi giá trị hiện tại mà không gây re-render
    const currentValueRef = useRef(internalValue);

    // Cập nhật internalValue khi prop value thay đổi từ bên ngoài
    useEffect(() => {
      if (value !== undefined && value !== currentValueRef.current) {
        setInternalValue(value);
        currentValueRef.current = value;
      }
    }, [value]);

    // Xử lý khi giá trị thay đổi - sử dụng cách tiếp cận uncontrolled để tránh mất focus
    const handleChange = (e) => {
      const newValue = e.target.value;
      // Cập nhật giá trị trong ref để theo dõi mà không gây re-render
      currentValueRef.current = newValue;
      // Cập nhật state nội bộ
      setInternalValue(newValue);

      // Chỉ gọi onChange nếu cần thiết và không gây re-render quá nhiều
      if (onChange) {
        // Tạo một event object mới để tránh React synthetic event pool issues
        const syntheticEvent = {
          target: {
            value: newValue,
          },
          currentTarget: e.currentTarget,
          preventDefault: e.preventDefault,
          stopPropagation: e.stopPropagation,
        };

        // Gọi onChange trực tiếp để tránh issues với setTimeout
        onChange(syntheticEvent);
      }
    };

    // Xử lý khi blur khỏi input
    const handleBlur = (e) => {
      if (onBlur) {
        // Đảm bảo giá trị cuối cùng được cập nhật
        const syntheticEvent = {
          target: {
            value: currentValueRef.current,
          },
          currentTarget: e.currentTarget,
          preventDefault: e.preventDefault,
          stopPropagation: e.stopPropagation,
        };
        onBlur(syntheticEvent);
      }
    };

    // Xử lý sự kiện focus để giữ focus khi click vào input
    const handleFocus = () => {
      // Không cần làm gì đặc biệt, chỉ cần đảm bảo sự kiện này được gắn vào input
    };

    // Xử lý sự kiện click trực tiếp trên input
    const handleClick = () => {
      // Đảm bảo input giữ được focus khi click
      if (inputRef.current && document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    };

    // Xử lý sự kiện keydown
    const handleKeyDown = (e) => {
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    return (
      <input
        ref={inputRef}
        type={type}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${className}`}
        value={internalValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
    );
  }
);

export default CustomInput;
